import {useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback} from "react";

import {findParentWithClass} from "libs/utils";
import {toStoredEditableText, fromStoredEditableText} from "libs/editableText";
import {editableTextContent} from "libs/contentEditableText";
import {ensureProductNameNbsp} from "libs/typograf";
import {useSetting} from "contexts/setting";
import {getTemplateMode, isScopesChannel, templatesInMode, withTemplateMode} from "libs/sceneScope";
import {useEdits} from "contexts/edits";
import {TEXT_CLASSNAMES, PLACEHOLDERS} from "data/constants";

/**
 * Map from text type → edits context field name.
 * Used to select ONLY the relevant text state for deps narrowing.
 */
const TYPE_TO_FIELD = {
  title: "titles",
  subtitle: "subtitles",
  subtitle2: "subtitles2",
  subtitle3: "subtitles3",
  name: "names",
  name2: "names2",
  name3: "names3",
  name4: "names4",
  name5: "names5",
  name6: "names6",
  badge: "badges",
  "gdn-button": "badges",
  position: "positions",
  position2: "positions2",
  date: "dates",
  time: "times",
  eap: "eaps",
  link: "links",
  link2: "links2",
  link3: "links3",
  editabletag: "tags",
  "gdn-tag": "gdnTags",
  features: "features",
  version: "versions",
  "product-name": "productNames",
};

/**
 * Map from text type → edit function name in the edits context.
 */
const TYPE_TO_EDITOR = {
  title: "editTitles",
  subtitle: "editSubtitles",
  subtitle2: "editSubtitles2",
  subtitle3: "editSubtitles3",
  name: "editNames",
  name2: "editNames2",
  name3: "editNames3",
  name4: "editNames4",
  name5: "editNames5",
  name6: "editNames6",
  badge: "editBadges",
  "gdn-button": "editBadges",
  position: "editPositions",
  position2: "editPositions2",
  date: "editDates",
  time: "editTimes",
  eap: "editEaps",
  link: "editLinks",
  link2: "editLinks2",
  link3: "editLinks3",
  editabletag: null, // special case: uses setTags
  "gdn-tag": "editGdnTags",
  features: "editFeatures",
  version: "editVersions",
  "product-name": "editProductName",
};

function isPlaceholderText(text, type) {
  if (!type || !text) return false;
  const cleanText = typeof text === "string" ? text.replace(/<[^>]*>/g, "").trim() : text;
  for (const lang in PLACEHOLDERS) {
    const placeholder = PLACEHOLDERS[lang][type];
    if (placeholder === text || placeholder === cleanText) return true;
  }
  return false;
}

/**
 * Decide whether onTextChange should write to `state[field][id]` (return
 * true) or `state[field].main` (return false).
 *
 * Releases templates resolve text as `field[id] ?? field.main ?? placeholder`,
 * so the write key must align with whichever key the data is currently
 * stored under — otherwise the user's typed value is masked on re-render
 * by an unchanged read at a different key, and the input visibly reverts.
 *
 * Rules:
 *   isLocked                          → write by id (locked = explicit per-template)
 *   value present at field[id]        → write by id (data is keyed by template)
 *   value present only at field.main  → write to main (default unlocked behavior)
 *   no entry either place             → write to main (initialize new edit)
 *
 * Empty / null / 0 values count as "present" — they're explicit data, not
 * absence. Only `undefined` triggers the main-write fallback.
 */
export function shouldUseIdKey(isLocked, fieldState, id) {
  return Boolean(isLocked || (fieldState && fieldState[id] !== undefined));
}

/**
 * Shared text state hook for Textarea and ContentEditable.
 *
 * Extracts type detection, text resolution, editing, placeholder detection,
 * lock/unlock handling, and font-size class computation.
 *
 * Effect deps are narrowed to only the relevant text field for this component's
 * type (e.g., a title Textarea's font-size effect only re-runs when titles change,
 * not when subtitles change). Note: the component still re-renders on any edits
 * context change — true subscription narrowing requires context selectors or
 * an external state library.
 *
 * @param {string} id - Template ID (e.g., "Twitter", "Splash")
 * @param {string} text - Text prop from parent (from context — titles[id] || titles.main)
 * @param {React.RefObject} labelRef - Ref to the label DOM element for type detection
 * @returns {{type, currentText, onTextChange, fontSizeClass, isPlaceholder, isLocked}}
 */
export function useTextState(id, text, labelRef) {
  const [type, setType] = useState("");
  const wasLockedRef = useRef(false);
  const [lockedText, setLockedText] = useState(text);
  const [fontSizeClass, setFontSizeClass] = useState("");

  const {locked, templateModes, editTemplateModes, dataChannel} = useSetting();

  const edits = useEdits();
  const {cleanupLocalChanges, setTags} = edits;

  // The chain, or — on a scopes channel — a text mode of the banner's own:
  // `own` (the user set it apart) or `preset` (the default scene gave it a
  // text). Both read and write the banner's own key. See libs/sceneScope.ts.
  const scoped = isScopesChannel(dataChannel);
  const textMode = scoped ? getTemplateMode(templateModes, id, "text") : undefined;
  const isLocked = locked.some((elem) => elem === id) || textMode !== undefined;

  // Detect type from DOM parent BEFORE paint — useLayoutEffect ensures type
  // is set synchronously after DOM mutation, so the first interactive render
  // already has the correct type (no dropped text changes on type="").
  useLayoutEffect(() => {
    if (labelRef.current) {
      const t = findParentWithClass(labelRef.current, TEXT_CLASSNAMES);
      setType(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- labelRef is stable (useRef), runs once on mount

  // Get ONLY the relevant text state and editor for this component's type.
  // Indexed directly from edits via TYPE_TO_FIELD/TYPE_TO_EDITOR maps —
  // no intermediate objects created per render.
  const fieldName = TYPE_TO_FIELD[type];
  const fieldState = fieldName ? edits[fieldName] : undefined;
  const relevantLocalText = isLocked && fieldState ? fieldState[id] : undefined;

  const editorName = TYPE_TO_EDITOR[type];
  const editFn = editorName ? edits[editorName] : null;

  const yieldTextPresets = useCallback(() => {
    const presetIds = templatesInMode(templateModes, "text", "preset");
    if (!presetIds.length) return;
    let modes = templateModes;
    for (const presetId of presetIds) {
      cleanupLocalChanges(presetId);
      modes = withTemplateMode(modes, presetId, "text", undefined);
    }
    editTemplateModes(modes);
  }, [templateModes, editTemplateModes, cleanupLocalChanges]);

  // Resolve current text. Product-name nbsp is enforced here — the single
  // value both Textarea and ContentEditable display and edit — so "IntelliJ
  // IDEA" is bound on every render regardless of the Typograf toggle, whether
  // the text was typed, loaded from a saved scene, or never blurred. This is a
  // hard exception that overrides Typograf: the product name must never wrap.
  const currentText = useMemo(() => {
    const resolved = !isLocked
      ? text
      : relevantLocalText !== undefined
        ? relevantLocalText
        : lockedText;
    return ensureProductNameNbsp(fromStoredEditableText(resolved));
  }, [isLocked, text, relevantLocalText, lockedText]);

  // Check if text is a placeholder
  const isPlaceholder = useMemo(() => isPlaceholderText(currentText, type), [currentText, type]);

  // Edit handler — depends only on the specific edit function + field state for this type
  const onTextChange = useCallback(
    (newValue) => {
      const storedValue = toStoredEditableText(newValue);
      const useIdKey = shouldUseIdKey(isLocked, fieldState, id);

      if (editFn) {
        editFn(storedValue, id, useIdKey);
        if (scoped) {
          if (useIdKey && textMode === "preset") {
            // Editing a preset makes it the banner's own: shared changes no
            // longer take it away.
            editTemplateModes(withTemplateMode(templateModes, id, "text", "own"));
          } else if (!useIdKey) {
            // A shared edit: every preset text yields to it, and only presets —
            // banners the user set apart keep theirs.
            yieldTextPresets();
          }
        }
      } else if (type === "editabletag" && setTags) {
        setTags((prev) => ({...prev, [id]: newValue}));
      }
    },
    [type, id, isLocked, fieldState, editFn, setTags, scoped, textMode, templateModes, editTemplateModes, yieldTextPresets]
  );

  // Lock/unlock effect
  useEffect(() => {
    if (!wasLockedRef.current && isLocked) {
      setLockedText(text);
    } else if (wasLockedRef.current && !isLocked) {
      cleanupLocalChanges(id);
      setFontSizeClass("");
    }
    wasLockedRef.current = isLocked;
  }, [isLocked, id, text, cleanupLocalChanges]);

  // Font-size class computation — NARROW deps: only currentText, not all fields
  useEffect(() => {
    if (isLocked && type === "title") {
      const content = editableTextContent(currentText);
      const lineBreaks = (content.match(/\n/g) || []).length;
      const titleLength = content.length;

      if (lineBreaks >= 4 || titleLength >= 65) {
        setFontSizeClass("font-size-small");
      } else if (lineBreaks >= 3 || (titleLength >= 50 && titleLength < 65)) {
        setFontSizeClass("font-size-medium");
      } else if (lineBreaks < 3 && titleLength < 50) {
        setFontSizeClass("font-size-large");
      }
    } else if (!isLocked) {
      setFontSizeClass("");
    }
  }, [isLocked, type, currentText]);

  return {
    type,
    currentText,
    onTextChange,
    fontSizeClass,
    isPlaceholder,
    isLocked,
  };
}
